import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster, ToastBar } from 'react-hot-toast';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { TenantProvider } from './contexts/TenantContext';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { ProjectProvider } from './contexts/ProjectContext';
import { TerminologyProvider } from './contexts/TerminologyContext';
import { OnboardingProvider } from './contexts/OnboardingContext';
import { NotificationProvider } from './contexts/NotificationContext';
import PWAInstallPrompt from './components/PWAInstallPrompt';

// ✅ Create React Query client
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 0, // Always consider data stale for fresh fetches
    },
  },
});
import TenantLoader from './components/TenantLoader';
import ProtectedRoute from './components/ProtectedRoute';
import RoleProtectedRoute from './components/RoleProtectedRoute';
import Layout from './components/Layout';
import Login from './pages/Login';
import OAuthConsent from './pages/OAuthConsent';
import Register from './pages/Register';
import ForgotPassword from './pages/ForgotPassword';
import ResetPassword from './pages/ResetPassword';
import ForcedPasswordChange from './pages/ForcedPasswordChange';
import MFASetup from './pages/MFASetup';
import Dashboard from './pages/Dashboard';
import Documents from './pages/Documents';
import Analysis from './pages/Analysis';
import ProcessMaps from './pages/ProcessMaps';
import SopGeneration from './pages/SopGeneration';
import Interviews from './pages/Interviews';
import RiskMapping from './pages/RiskMapping';
import LogicDecomposerDashboard from './components/LogicDecomposer/LogicDecomposerDashboard';
import ConversionArchitect from './pages/ConversionArchitect';
import RiskTemplateQC from './pages/RiskTemplateQC';
import SOPBuilder from './pages/SOPBuilder';
import RCSAProcessSteps from './pages/RCSAProcessSteps';
import ProcessOptimization from './pages/ProcessOptimization';
import RCSAProcessBuilder from './pages/RCSAProcessBuilder';
import RACIMatrix from './pages/RACIMatrix';
import ApprovalWorkflow from './components/ChangeDetection/ApprovalWorkflow';
import { useProject } from './contexts/ProjectContext';
import Admin from './pages/Admin';
import Settings from './pages/Settings';
import Integrations from './pages/Integrations';
import TenantManagement from './pages/TenantManagement';
import TenantSignup from './pages/TenantSignup';
import TenantRequests from './pages/TenantRequests';
import Landing from './pages/Landing';
import ExecutiveDashboard from './components/Dashboards/ExecutiveDashboard';
import ResidualRiskCalculator from './components/RCSA/ResidualRiskCalculator';
import FAQHelp from './pages/FAQHelp';
import FAQManagement from './pages/FAQManagement';
import BrandingSettings from './pages/BrandingSettings';
import AuditDashboard from './pages/AuditDashboard';
import EvidenceHarvesting from './pages/EvidenceHarvesting';
import ComplianceDrift from './pages/ComplianceDrift';
import ControlMappingPage from './pages/ControlMapping';
import VendorManagement from './pages/VendorManagement';
import SecurityDocuments from './pages/SecurityDocuments';
import AICouncil from './pages/AICouncil';
import VerificationDashboard from './pages/VerificationDashboard';
import VerificationConfig from './pages/VerificationConfig';
import AccuracyTestSuite from './pages/AccuracyTestSuite';
import ProjectManagement from './pages/ProjectManagement';
import Intelligence from './pages/Intelligence';
// Hidden/beta module — deliberately NOT added to components/Navigation.tsx.
// Reachable only via direct link at /app/sales-call-coach.
import SalesCallCoach from './pages/SalesCallCoach';
import SmartStart from './pages/SmartStart';
import SmartStartPipelineAdmin from './pages/SmartStartPipelineAdmin';
import VerificationFeedbackAdmin from './pages/VerificationFeedbackAdmin';
import StrategicTools from './pages/StrategicTools';
import InvestmentAnalysis from './pages/InvestmentAnalysis';
import CompanyBrain from './pages/CompanyBrain';
import BDEngagementDetail from './pages/BDEngagementDetail';
import BusinessDevelopment from './pages/BusinessDevelopment';
import BDMeetingDetail from './pages/BDMeetingDetail';
import ComplianceDashboard from './pages/ComplianceDashboard';
import GlobalAlertInbox from './pages/GlobalAlertInbox';
import GFDIntakeDemoPage from './components/Intelligence/GFDIntake/GFDIntakeDemoPage';
import TasksPage from './pages/Tasks/TasksPage';

import './App.css';
import toast from 'react-hot-toast';

// Global state for pending process map updates
let pendingProcessMapUpdates = new Set<string>();

// Utility functions for managing pending process map updates
export const procesMapUpdateManager = {
  // Check if there are pending updates for a specific process map
  hasPendingUpdate: (processMapId: string): boolean => {
    return pendingProcessMapUpdates.has(processMapId);
  },

  // Check if there are any pending updates
  hasPendingUpdates: (): boolean => {
    return pendingProcessMapUpdates.size > 0;
  },

  // Get all pending update IDs
  getPendingUpdates: (): string[] => {
    return Array.from(pendingProcessMapUpdates);
  },

  // Mark a process map update as processed (remove from pending)
  markAsProcessed: (processMapId: string): void => {
    pendingProcessMapUpdates.delete(processMapId);

    // Notify app component about state change
    window.dispatchEvent(new CustomEvent('pendingUpdatesChanged', {
      detail: { hasPendingUpdates: pendingProcessMapUpdates.size > 0, count: pendingProcessMapUpdates.size }
    }));
  },

  // Clear all pending updates
  clearAll: (): void => {
    pendingProcessMapUpdates.clear();

    // Notify app component about state change
    window.dispatchEvent(new CustomEvent('pendingUpdatesChanged', {
      detail: { hasPendingUpdates: false, count: 0 }
    }));
  }
};

// Role-based landing redirect — all users land on Smart Router
const LandingRedirect: React.FC = () => {
  return <Navigate to="/app/smart-start" replace />;
};

// Guard that redirects non-admins away from admin-only pages
const AdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN' || user?.role === 'MASTER' || user?.role === 'SUPER_ADMIN';
  if (!isAdmin) return <Navigate to="/app/smart-start" replace />;
  return <>{children}</>;
};

// Wrapper component for ChangeDetection that provides projectId
const ChangeDetectionWrapper: React.FC = () => {
  const { currentProject } = useProject();

  if (!currentProject) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-gray-600">Please select a project to use Change Detection</p>
        </div>
      </div>
    );
  }

  return <ApprovalWorkflow projectId={currentProject.id} />;
};

function App() {
  const [hasPendingUpdates, setHasPendingUpdates] = useState(false);

  useEffect(() => {
  // Global event listener for process map updates
  const handleProcessMapUpdate = (event: CustomEvent) => {
    const { processMapId } = event.detail;

    if (processMapId) {
      pendingProcessMapUpdates.add(processMapId);

      // Notify app component about new pending update
      window.dispatchEvent(new CustomEvent('pendingUpdatesChanged', {
        detail: { hasPendingUpdates: true, count: pendingProcessMapUpdates.size }
      }));

      // Dispatch a global state change event for any components that need to react
      window.dispatchEvent(new CustomEvent('globalProcessMapUpdateReceived', {
        detail: { processMapId, pendingUpdates: Array.from(pendingProcessMapUpdates) }
      }));
    }
  };

  // Listen for pending updates state changes
  const handlePendingUpdatesChange = (event: CustomEvent) => {
    const { hasPendingUpdates: hasUpdates, count } = event.detail;
    setHasPendingUpdates(hasUpdates);

    // Show toast notification when new updates are pending
    if (hasUpdates && count === 1) {
      toast('🔄 Process map changes pending. Visit Process Maps page to see updates.', {
        duration: 6000,
        icon: '📋',
        style: {
          background: '#3b82f6',
          color: 'white',
        },
      });
    }
  };

  window.addEventListener('processMapUpdated', handleProcessMapUpdate as EventListener);
  window.addEventListener('pendingUpdatesChanged', handlePendingUpdatesChange as EventListener);

  return () => {
    window.removeEventListener('processMapUpdated', handleProcessMapUpdate as EventListener);
    window.removeEventListener('pendingUpdatesChanged', handlePendingUpdatesChange as EventListener);
  };
}, []);

  if (typeof window !== 'undefined' && window.location.pathname.startsWith('/gfd-intake-demo')) {
    return (
      <Router>
        <Routes>
          <Route path="/gfd-intake-demo" element={<GFDIntakeDemoPage />} />
        </Routes>
      </Router>
    );
  }

  return (
    <QueryClientProvider client={queryClient}>
      <TenantProvider>
        <TenantLoader>
          <AuthProvider>
            <Router>
            <div className="App">
          <Toaster
            position="bottom-right"
            toastOptions={{
              duration: 5000,
              style: {
                background: '#363636',
                color: '#fff',
                borderRadius: '8px',
                fontSize: '14px',
                padding: '16px',
              },
              success: {
                style: {
                  background: '#10b981',
                },
                iconTheme: {
                  primary: '#fff',
                  secondary: '#10b981',
                },
              },
              error: {
                style: {
                  background: '#ef4444',
                },
                iconTheme: {
                  primary: '#fff',
                  secondary: '#ef4444',
                },
              },
              loading: {
                style: {
                  background: '#3b82f6',
                },
              },
            }}
          >
            {(t) => (
              <ToastBar toast={t}>
                {({ icon, message }) => (
                  <>
                    {icon}
                    {message}
                    {t.type !== 'loading' && (
                      <button
                        type="button"
                        aria-label="Dismiss notification"
                        onClick={() => toast.dismiss(t.id)}
                        style={{
                          background: 'transparent',
                          border: 'none',
                          color: 'inherit',
                          cursor: 'pointer',
                          opacity: 0.7,
                          fontSize: '16px',
                          lineHeight: 1,
                          padding: '0 0 0 4px',
                          flexShrink: 0,
                        }}
                      >
                        ×
                      </button>
                    )}
                  </>
                )}
              </ToastBar>
            )}
          </Toaster>

          {/* Global pending updates indicator */}
          {hasPendingUpdates && (
            <div
              className="fixed top-4 right-4 z-50 bg-blue-500 text-white px-4 py-2 rounded-lg shadow-lg cursor-pointer transition-all hover:bg-blue-600"
              onClick={() => {
                window.location.href = '/app/process-maps';
              }}
              title="Click to view pending process map updates"
            >
              <div className="flex items-center space-x-2">
                <div className="w-2 h-2 bg-white rounded-full animate-pulse"></div>
                <span className="text-sm font-medium">Process Map Updates Pending</span>
                <div className="text-xs bg-white text-blue-500 px-1.5 py-0.5 rounded">
                  {procesMapUpdateManager.getPendingUpdates().length}
                </div>
              </div>
            </div>
          )}
          
          <Routes>
            <Route path="/home" element={<Landing />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forced-password-change" element={<ForcedPasswordChange />} />
            <Route path="/mfa-setup" element={<MFASetup />} />
            <Route path="/register" element={<Register />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/signup" element={<TenantSignup />} />
            <Route path="/oauth/authorize" element={<OAuthConsent />} />
            
            {/* Protected app routes */}
            <Route path="/app" element={
              <ProtectedRoute>
                <NotificationProvider>
                  <ProjectProvider>
                    <TerminologyProvider>
                      <OnboardingProvider>
                        <Layout />
                      </OnboardingProvider>
                    </TerminologyProvider>
                  </ProjectProvider>
                </NotificationProvider>
              </ProtectedRoute>
            }>
              <Route index element={<LandingRedirect />} />
              <Route path="alert-inbox" element={<RoleProtectedRoute requiredRole="SUPER_ADMIN"><GlobalAlertInbox /></RoleProtectedRoute>} />
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="documents" element={<Documents />} />
              <Route path="analysis" element={<Analysis />} />
              <Route path="process-maps" element={<ProcessMaps />} />
              <Route path="sop-builder" element={<SOPBuilder />} />
              <Route path="sop-generation" element={<SopGeneration />} />
              <Route path="interviews" element={<Interviews />} />
              <Route path="risk-mapping" element={<RiskMapping />} />
              <Route path="risk-analyzer" element={<Navigate to="../risk-mapping" replace />} />
              <Route path="raci-matrix" element={<RACIMatrix />} />
              <Route path="risk-template-qc" element={<RiskTemplateQC />} />
              <Route path="process-optimization" element={<ProcessOptimization />} />
              <Route path="branding-settings/:projectId" element={<BrandingSettings />} />
              <Route path="change-detection" element={<ChangeDetectionWrapper />} />
              <Route path="rcsa-process-steps/:projectId" element={<RCSAProcessSteps />} />
              <Route path="rcsa-process-builder" element={<RCSAProcessBuilder />} />
              <Route path="executive-dashboard" element={<ExecutiveDashboard />} />
              <Route path="logic-decomposer" element={<LogicDecomposerDashboard />} />
              <Route path="conversion-architect" element={<ConversionArchitect />} />
              <Route path="admin" element={<AdminRoute><Admin /></AdminRoute>} />
              <Route path="tenant-management" element={
                <RoleProtectedRoute requiredRole="SUPER_ADMIN">
                  <TenantManagement />
                </RoleProtectedRoute>
              } />
              <Route path="tenant-requests" element={
                <RoleProtectedRoute requiredRole="SUPER_ADMIN">
                  <TenantRequests />
                </RoleProtectedRoute>
              } />
              <Route path="settings" element={<Settings />} />
              {/* Legacy Integrations URL: redirects into Admin → Identity & Setup
                  → Integrations (preserves OAuth query params). Admin-only and
                  Stemmler-only; tenant gate is inside the page. */}
              <Route path="integrations" element={
                <RoleProtectedRoute requiredRole="ADMIN">
                  <Integrations />
                </RoleProtectedRoute>
              } />
              <Route path="faq" element={<FAQHelp />} />
              <Route path="faq-management" element={<FAQManagement />} />
              <Route path="audit-dashboard" element={
                <RoleProtectedRoute requiredRole="ADMIN">
                  <AuditDashboard />
                </RoleProtectedRoute>
              } />
              <Route path="evidence-harvesting" element={<EvidenceHarvesting />} />
              <Route path="compliance-drift" element={<ComplianceDrift />} />
              <Route path="control-mapping" element={<ControlMappingPage />} />
              <Route path="vendor-management" element={<VendorManagement />} />
              <Route path="ai-council" element={<AICouncil />} />
              <Route path="verification-dashboard" element={
                <RoleProtectedRoute requiredRole="SUPER_ADMIN">
                  <VerificationDashboard />
                </RoleProtectedRoute>
              } />
              <Route path="verification-config" element={
                <RoleProtectedRoute requiredRole="SUPER_ADMIN">
                  <VerificationConfig />
                </RoleProtectedRoute>
              } />
              <Route path="accuracy-test-suite" element={
                <RoleProtectedRoute requiredRole="SUPER_ADMIN">
                  <AccuracyTestSuite />
                </RoleProtectedRoute>
              } />
              <Route path="project-management" element={<ProjectManagement />} />
              <Route path="security-documents" element={
                <RoleProtectedRoute requiredRole="SUPER_ADMIN">
                  <SecurityDocuments />
                </RoleProtectedRoute>
              } />
              <Route path="intelligence" element={<Intelligence />} />
              {/* Hidden/beta — no Navigation.tsx entry, direct-link only */}
              <Route path="sales-call-coach" element={<SalesCallCoach />} />
              <Route path="smart-start" element={<SmartStart />} />
              <Route path="smart-start/:sessionId" element={<SmartStart />} />
              <Route path="smart-start-admin" element={
                <RoleProtectedRoute requiredRole="ADMIN">
                  <SmartStartPipelineAdmin />
                </RoleProtectedRoute>
              } />
              <Route path="verification-feedback" element={
                <RoleProtectedRoute requiredRole="SUPER_ADMIN">
                  <VerificationFeedbackAdmin />
                </RoleProtectedRoute>
              } />
              <Route path="strategic-tools" element={<StrategicTools />} />
              <Route path="investment-analysis" element={<InvestmentAnalysis />} />
              <Route path="company-brain" element={
                <RoleProtectedRoute requiredRole="ADMIN">
                  <CompanyBrain />
                </RoleProtectedRoute>
              } />
              <Route path="business-development" element={<BusinessDevelopment />} />
              <Route path="bd-engagements/:id" element={<BDEngagementDetail />} />
              <Route path="bd-meetings/:id" element={<BDMeetingDetail />} />
              <Route path="bd-engagements" element={<Navigate to="/app/business-development?tab=engagements" replace />} />
              <Route path="bd-approvals" element={<Navigate to="/app/business-development?tab=approvals" replace />} />
              <Route path="ai-compliance" element={
                <RoleProtectedRoute requiredRole="ADMIN">
                  <ComplianceDashboard />
                </RoleProtectedRoute>
              } />
              <Route path="tasks" element={<TasksPage />} />
              <Route path="tasks/*" element={<TasksPage />} />
              <Route path="*" element={<LandingRedirect />} />
            </Route>

            {/* Root redirect */}
            <Route path="/" element={<Navigate to="/home" replace />} />
            <Route path="*" element={<Navigate to="/home" replace />} />
          </Routes>
              <PWAInstallPrompt />
            </div>
            </Router>
          </AuthProvider>
        </TenantLoader>
      </TenantProvider>
    </QueryClientProvider>
  );
}

export default App;